import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SystemAnalytics, TrendData, PerformanceMetrics } from '../types';
import { analyticsApi } from '../services/api/analytics';
import { queryKeys } from '../lib/queryKeys';

export interface AnalyticsOptions {
  onAnalyticsUpdate?: (analytics: SystemAnalytics) => void;
}

export interface AnalyticsReturn {
  analytics: SystemAnalytics | null;
  loading: boolean;
  error: string | null;
  refreshAnalytics: () => void;
  getTrendData: (metric: string, period: string) => TrendData | null;
  getPerformanceMetrics: () => PerformanceMetrics | null;
}

export const useAnalytics = (options: AnalyticsOptions = {}): AnalyticsReturn => {
  const queryClient = useQueryClient();

  const { data: analytics = null, isLoading, error } = useQuery({
    queryKey: queryKeys.analytics.system,
    queryFn: () => analyticsApi.getSystemAnalytics(),
  });

  const refreshAnalytics = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.analytics.system });
  }, [queryClient]);

  const getTrendData = useCallback((metric: string, _period: string): TrendData | null => {
    if (!analytics?.trends) return null;
    return analytics.trends.find((trend: TrendData) => trend.metric === metric) || null;
  }, [analytics]);

  const getPerformanceMetrics = useCallback((): PerformanceMetrics | null => {
    return analytics?.performance || null;
  }, [analytics]);

  return {
    analytics,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refreshAnalytics,
    getTrendData,
    getPerformanceMetrics,
  };
};
